/**
 * Lógica de búsqueda del buscador de DataTable, extraída como funciones puras
 * para poder cubrirla con tests (el resto del componente es React).
 *
 * Modos soportados:
 *   "texto exacto"   → busca la frase entre comillas tal cual
 *   a / b / c        → OR: basta con que la fila contenga uno de los términos
 *   a b              → AND: la fila debe contener todos los términos
 *   a                → un solo término: contains simple
 */

/** Columna vista por el buscador (subconjunto del Column<T> de DataTable). */
export type SearchableColumn<T> = {
  key: string;
  searchValue?: (row: T) => string | number | null | undefined;
  sortValue?: (row: T) => string | number | null | undefined;
};

/**
 * Valores buscables de una fila, uno por columna.
 *
 * Prioridad: `searchValue` explícito → propiedad `row[key]` → `sortValue`.
 * El último eslabón es el que rescata las columnas con key SINTÉTICA (las que
 * no existen como propiedad de la fila, p. ej. 'producto' en el listado de
 * cotizaciones): sin él, el buscador nunca veía lo que esas columnas pintan
 * (BUG-027).
 */
export function rowSearchValues<T extends Record<string, unknown>>(
  row: T,
  columns: SearchableColumn<T>[],
): string[] {
  return columns.map((c) => {
    let v: unknown;
    if (c.searchValue) v = c.searchValue(row);
    else if (Object.prototype.hasOwnProperty.call(row, c.key)) v = row[c.key];
    else v = c.sortValue?.(row);
    return v !== null && v !== undefined ? String(v).toLowerCase() : '';
  });
}

export type SearchQuery = { mode: 'or' | 'and' | 'exact'; terms: string[] };

/** Interpreta el texto del buscador en modo + términos ya normalizados. */
export function parseSearchQuery(raw: string): SearchQuery {
  const q = raw.trim();
  if (q.startsWith('"') && q.endsWith('"') && q.length > 2) {
    return { mode: 'exact', terms: [q.slice(1, -1).toLowerCase()] };
  }
  if (q.includes('/')) {
    return { mode: 'or', terms: q.split('/').map((t) => t.trim().toLowerCase()).filter(Boolean) };
  }
  const terms = q.toLowerCase().split(/\s+/).filter(Boolean);
  return { mode: terms.length <= 1 ? 'exact' : 'and', terms };
}

/** ¿Casan los valores de una fila con la consulta? */
export function matchesSearch(values: string[], { mode, terms }: SearchQuery): boolean {
  if (terms.length === 0) return true;
  if (mode === 'exact') return values.some((v) => v.includes(terms[0]));
  if (mode === 'and') return terms.every((t) => values.some((v) => v.includes(t)));
  return terms.some((t) => values.some((v) => v.includes(t)));
}
